import socketService from "../services/chatSocketService";

// WebRTC configuration
const PC_CONFIG = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
    {
      urls: [
        "turn:turn.adactive.asia:3478?transport=udp",
        "turn:turn.adactive.asia:3478?transport=tcp",
      ],
      username: "webrtc",
      credential: "t1Zz99IQERsYCCggvL8Y4bFChtKVkBZ",
    },
  ],
};

class WebRTCService {
  constructor() {
    this.peerConnection = null;
    this.localStream = null;
    this.remoteStream = null;
    this.callbacks = {};
    this.isConnected = false;
    this.isInitiator = false;
  }

  /**
   * Initialize WebRTC service
   */
  async initialize() {
    try {
      // Setup socket event listeners for WebRTC
      this.setupSocketListeners();
      console.log("WebRTC service initialized");
      return true;
    } catch (error) {
      console.error("Failed to initialize WebRTC service:", error);
      this.handleError("Failed to initialize WebRTC service: " + error.message);
      return false;
    }
  }

  /**
   * Setup socket event listeners for WebRTC signaling
   */
  setupSocketListeners() {
    // Listen for incoming offers
    socketService.onOffer(async (data) => {
      console.log("Received WebRTC offer");
      await this.handleOffer(data);
    });

    // Listen for incoming answers
    socketService.onAnswer(async (data) => {
      console.log("Received WebRTC answer");
      await this.handleAnswer(data);
    });

    // Listen for ICE candidates
    socketService.onIceCandidate(async (data) => {
      console.log("Received ICE candidate");
      await this.handleIceCandidate(data);
    });

    // Listen for call ready events
    socketService.onCallReady((data) => {
      console.log("Call ready");
      this.createOffer();
    });

    // Listen for call ended events
    socketService.onCallEnded((data) => {
      console.log("Call ended by remote user");
      this.endCall(false);
    });
  }

  /**
   * Get user media (camera and microphone)
   */
  async getUserMedia(constraints = { video: true, audio: true }) {
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia(constraints);

      if (this.callbacks.onLocalStream) {
        this.callbacks.onLocalStream(this.localStream);
      }

      return this.localStream;
    } catch (error) {
      console.error("Error accessing media devices:", error);
      this.handleError("Could not access camera/microphone: " + error.message);
      throw error;
    }
  }

  /**
   * Create peer connection
   */
  createPeerConnection() {
    console.log("Creating peer connection...");
    this.peerConnection = new RTCPeerConnection(PC_CONFIG);

    // Add local stream to peer connection
    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => {
        this.peerConnection.addTrack(track, this.localStream);
      });
    }

    // Handle remote stream
    this.peerConnection.ontrack = (event) => {
      console.log("Received remote stream");
      this.remoteStream = event.streams[0];
      if (this.callbacks.onRemoteStream) {
        this.callbacks.onRemoteStream(this.remoteStream);
      }
    };

    // Handle ICE candidates
    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        console.log("Sending ICE candidate");
        socketService.sendIceCandidate({
          candidate: event.candidate,
        });
      }
    };

    // Handle connection state changes
    this.peerConnection.onconnectionstatechange = () => {
      console.log("Connection state:", this.peerConnection.connectionState);

      if (this.callbacks.onConnectionStateChange) {
        this.callbacks.onConnectionStateChange(
          this.peerConnection.connectionState
        );
      }

      if (this.peerConnection.connectionState === "connected") {
        this.isConnected = true;
      } else if (this.peerConnection.connectionState === "failed") {
        this.isConnected = false;
        this.handleError("Connection failed");
      }
    };

    // Handle ICE connection state changes
    this.peerConnection.oniceconnectionstatechange = () => {
      console.log(
        "ICE connection state:",
        this.peerConnection.iceConnectionState
      );

      if (
        this.peerConnection.iceConnectionState === "disconnected" ||
        this.peerConnection.iceConnectionState === "failed"
      ) {
        this.isConnected = false;
        this.handleError("Connection lost");
      }
    };
  }

  /**
   * Create and send offer (for initiating calls)
   */
  async createOffer() {
    try {
      if (!this.localStream) {
        await this.getUserMedia();
      }

      this.createPeerConnection();
      this.isInitiator = true;

      const offer = await this.peerConnection.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true,
      });

      await this.peerConnection.setLocalDescription(offer);

      socketService.sendOffer({ offer });
      console.log("Offer sent");
    } catch (error) {
      console.error("Error creating offer:", error);
      this.handleError("Failed to create offer: " + error.message);
    }
  }

  /**
   * Handle received offer (answer side)
   */
  async handleOffer(data) {
    try {
      console.log("Handling offer from remote peer");

      if (!this.localStream) {
        await this.getUserMedia();
      }

      if (!this.peerConnection) {
        this.createPeerConnection();
      }

      await this.peerConnection.setRemoteDescription(
        new RTCSessionDescription(data.offer)
      );

      const answer = await this.peerConnection.createAnswer();
      await this.peerConnection.setLocalDescription(answer);

      socketService.sendAnswer({ answer });
      console.log("Answer sent");

      if (this.callbacks.onCallStarted) {
        this.callbacks.onCallStarted();
      }
    } catch (error) {
      console.error("Error handling offer:", error);
      this.handleError("Failed to handle offer: " + error.message);
    }
  }

  /**
   * Handle received answer (initiator side)
   */
  async handleAnswer(data) {
    try {
      console.log("Handling answer from remote peer");

      if (this.peerConnection) {
        await this.peerConnection.setRemoteDescription(
          new RTCSessionDescription(data.answer)
        );
        console.log("Answer processed");

        if (this.callbacks.onCallStarted) {
          this.callbacks.onCallStarted();
        }
      }
    } catch (error) {
      console.error("Error handling answer:", error);
      this.handleError("Failed to handle answer: " + error.message);
    }
  }

  /**
   * Handle received ICE candidate
   */
  async handleIceCandidate(data) {
    try {
      if (this.peerConnection && data.candidate) {
        await this.peerConnection.addIceCandidate(
          new RTCIceCandidate(data.candidate)
        );
        console.log("ICE candidate added");
      }
    } catch (error) {
      console.error("Error adding ICE candidate:", error);
    }
  }

  /**
   * Toggle video track
   */
  toggleVideo() {
    if (this.localStream) {
      const videoTrack = this.localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        return videoTrack.enabled;
      }
    }
    return false;
  }

  /**
   * Toggle audio track
   */
  toggleAudio() {
    if (this.localStream) {
      const audioTrack = this.localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        return audioTrack.enabled;
      }
    }
    return false;
  }

  /**
   * End call
   */
  endCall(notifyRemote = true) {
    console.log("Ending call...");

    // Stop local stream
    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => track.stop());
      this.localStream = null;
    }

    // Close peer connection
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    // Notify remote user
    if (notifyRemote) {
      socketService.endCall();
    }

    // Reset state
    this.remoteStream = null;
    this.isConnected = false;
    this.isInitiator = false;

    if (this.callbacks.onCallEnded) {
      this.callbacks.onCallEnded();
    }
  }

  /**
   * Set callbacks for various events
   */
  setCallbacks(callbacks) {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Handle errors
   */
  handleError(message) {
    console.error("WebRTC Error:", message);
    if (this.callbacks.onError) {
      this.callbacks.onError(message);
    }
  }

  /**
   * Get local stream
   */
  getLocalStream() {
    return this.localStream;
  }

  /**
   * Get remote stream
   */
  getRemoteStream() {
    return this.remoteStream;
  }

  /**
   * Check if call is active
   */
  isCallActive() {
    return (
      this.isConnected &&
      this.peerConnection &&
      this.peerConnection.connectionState === "connected"
    );
  }

  /**
   * Get connection statistics
   */
  async getStats() {
    if (this.peerConnection) {
      return await this.peerConnection.getStats();
    }
    return null;
  }

  /**
   * Get current call state
   */
  getCallState() {
    return {
      isConnected: this.isConnected,
      isInitiator: this.isInitiator,
      connectionState: this.peerConnection?.connectionState,
      iceConnectionState: this.peerConnection?.iceConnectionState,
    };
  }
}

// Create and export singleton instance
const webrtcService = new WebRTCService();
export default webrtcService;
